import {Directive, HostBinding, HostListener, inject} from '@angular/core';
import {Tooltip} from 'primeng/tooltip';

/**
 * Info bubble behaviour for the icon next to a dashboard tile's label, placed on the same element as its pTooltip.
 * <p>
 * The bubble is opened by this directive rather than by PrimeNG's own triggers, which close it on the click that follows
 * a tap. A mouse opens it on hover, and a click, a tap or Enter/Space toggles it so it stays open until the icon loses
 * focus. It opens above the icon on a narrow touch screen, and to its right with a mouse.
 */
@Directive({
    selector: '[infoTip]',
    standalone: false
})
export class InfoTipDirective
{
    @HostBinding('attr.role') readonly role = 'button';
    @HostBinding('attr.tabindex') readonly tabindex = 0;

    private readonly tooltip = inject(Tooltip);

    /** Whether a click, tap or key press opened the bubble, so moving the mouse away should not close it. */
    private pinned = false;

    constructor()
    {
        this.tooltip.setOption({
            tooltipEvent: 'manual',
            tooltipPosition: window.matchMedia?.('(hover: none)').matches ? 'top' : 'right'
        });
    }

    /**
     * Opens the bubble when a mouse moves onto the icon. A closed bubble may have been closed by PrimeNG's own Escape
     * handler, which skips {@link closeInfo}, so the pin is cleared first.
     */
    @HostListener('pointerenter', ['$event'])
    onPointerEnter(event: PointerEvent): void
    {
        if (event.pointerType !== 'mouse')
        {
            return;
        }
        if (!this.tooltip.container)
        {
            this.pinned = false;
            this.tooltip.deactivate();
        }
        this.tooltip.activate();
    }

    /** Closes the bubble when a mouse leaves the icon, unless a click opened it. */
    @HostListener('pointerleave', ['$event'])
    onPointerLeave(event: PointerEvent): void
    {
        if (event.pointerType === 'mouse' && !this.pinned)
        {
            this.tooltip.deactivate();
        }
    }

    /** Opens the bubble if it is closed and closes it if it is open. */
    @HostListener('click')
    @HostListener('keydown.enter', ['$event'])
    @HostListener('keydown.space', ['$event'])
    toggleInfo(event?: Event): void
    {
        event?.preventDefault();
        if (this.tooltip.container)
        {
            if (this.pinned)
            {
                this.closeInfo();
            }
            else
            {
                this.pinned = true;
            }
            return;
        }
        this.pinned = true;
        this.tooltip.deactivate();
        this.tooltip.activate();
    }

    /** Closes the bubble when the icon loses focus. */
    @HostListener('blur')
    closeInfo(): void
    {
        this.pinned = false;
        this.tooltip.deactivate();
    }
}
